import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  WifiIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import { useAppPrefix } from '../../AppPrefixContext';
import { toast } from 'react-hot-toast';
import { type ScoringMethod } from '../../../lib/scoring';

interface GroupPlayer {
  player_id: string;
  first_name: string;
  last_name: string;
  category: string;
  handicap: number;
  tee_time: string | null;
}

type HoleScores = Record<string, Record<number, string>>;

interface PenaltyEntry {
  penalty_strokes: number;
  reason: string;
}
type PenaltyMap = Record<string, Record<number, PenaltyEntry>>;

const LOCAL_KEY = (tid: string, round: number, gn: string) =>
  `scorer_draft_${tid}_r${round}_g${gn}`;

function loadLocalDraft(key: string): HoleScores | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveLocalDraft(key: string, scores: HoleScores) {
  try { localStorage.setItem(key, JSON.stringify(scores)); } catch {}
}

function clearLocalDraft(key: string) {
  try { localStorage.removeItem(key); } catch {}
}

function calcNetScore(totalStrokes: number, holesEntered: number, handicap: number, scoringMethod?: ScoringMethod): number | null {
  if (holesEntered === 0) return null;
  const parPerHole = 72 / 18;
  const proRatedPar = Math.round(parPerHole * holesEntered);
  const handicapAllowance = Math.floor(handicap * holesEntered / 18);
  if (scoringMethod === 'stableford_net') {
    const netStrokes = totalStrokes - handicapAllowance;
    const points = Math.max(0, 2 * holesEntered - (netStrokes - proRatedPar));
    return points;
  }
  return totalStrokes - proRatedPar - handicapAllowance;
}

function netScoreLabel(net: number | null, scoringMethod?: ScoringMethod): string {
  if (net === null) return '—';
  if (scoringMethod === 'stableford_net') return `${net} pts`;
  if (net === 0) return 'E';
  return net > 0 ? `+${net}` : `${net}`;
}

function netScoreColor(net: number | null, scoringMethod?: ScoringMethod): string {
  if (net === null) return 'text-gray-400';
  if (scoringMethod === 'stableford_net') {
    if (net >= 36) return 'text-emerald-600';
    if (net >= 30) return 'text-gray-700';
    return 'text-red-500';
  }
  if (net < 0) return 'text-emerald-600';
  if (net === 0) return 'text-gray-700';
  return 'text-red-500';
}

export default function ScorerGroupScoreEntry() {
  const { groupNumber } = useParams<{ groupNumber: string }>();
  const [searchParams] = useSearchParams();
  const paramRoundId = searchParams.get('roundId') || '';
  const paramRoundNumber = parseInt(searchParams.get('round') || '0');
  const p = useAppPrefix();
  const navigate = useNavigate();
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const scoringMethod: ScoringMethod = (selectedTournament?.scoring_method as ScoringMethod) || 'stroke_play_net';

  const [resolvedRoundId, setResolvedRoundId] = useState(paramRoundId);
  const [resolvedRoundNumber, setResolvedRoundNumber] = useState(paramRoundNumber);
  const [players, setPlayers] = useState<GroupPlayer[]>([]);
  const [holeScores, setHoleScores] = useState<HoleScores>({});
  const [remoteScores, setRemoteScores] = useState<HoleScores>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teeTime, setTeeTime] = useState('');
  const [startingHole, setStartingHole] = useState(1);
  const [activeHole, setActiveHole] = useState(1);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasDraft, setHasDraft] = useState(false);
  const [conflicts, setConflicts] = useState<{ playerId: string; hole: number }[]>([]);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [assignedHoles, setAssignedHoles] = useState<Set<number>>(new Set());
  const [penalties, setPenalties] = useState<PenaltyMap>({});
  const [penaltyModal, setPenaltyModal] = useState<{ playerId: string; hole: number } | null>(null);
  const [penaltyStrokes, setPenaltyStrokes] = useState(1);
  const [penaltyReason, setPenaltyReason] = useState('');
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftKey = LOCAL_KEY(tournamentId, resolvedRoundNumber || 1, groupNumber ?? '');

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!tournamentId || paramRoundId) return;
    (async () => {
      const { data } = await supabase
        .from('tournament_rounds')
        .select('id, round_number')
        .eq('tournament_id', tournamentId)
        .order('round_number')
        .limit(1);
      if (data && data.length > 0) {
        setResolvedRoundId(data[0].id);
        setResolvedRoundNumber(data[0].round_number);
      }
    })();
  }, [tournamentId, paramRoundId]);

  useEffect(() => {
    if (tournamentId && resolvedRoundId && groupNumber) {
      fetchGroupData();
      fetchAssignedHoles();
    }
  }, [tournamentId, resolvedRoundId, groupNumber]);

  const fetchAssignedHoles = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('scorer_assignments')
      .select('hole_number')
      .eq('round_id', resolvedRoundId)
      .eq('scorer_user_id', user.id);
    const holes = (data ?? []).map((a: { hole_number: number }) => a.hole_number);
    setAssignedHoles(new Set(holes));
    if (holes.length > 0) {
      const first = Math.min(...holes);
      setActiveHole(first);
    }
  };

  useEffect(() => {
    if (Object.keys(holeScores).length === 0) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveLocalDraft(draftKey, holeScores);
      setHasDraft(true);
      if (isOnline && !saving) handleSave(false, true);
    }, 1500);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [holeScores]);

  const fetchGroupData = async () => {
    setLoading(true);
    try {
      const { data: pairings, error } = await supabase
        .from('player_pairings')
        .select('*')
        .eq('round_id', resolvedRoundId)
        .eq('group_number', parseInt(groupNumber!))
        .order('sort_order');

      if (error) throw error;

      const playerIds = (pairings ?? []).map(pp => pp.player_id);
      if (playerIds.length === 0) { setLoading(false); return; }

      const { data: playerRows } = await supabase
        .from('players')
        .select('id, first_name, last_name, category, handicap')
        .in('id', playerIds);

      const playerMap = new Map((playerRows ?? []).map(pl => [pl.id, pl]));
      const groupPlayers: GroupPlayer[] = (pairings ?? []).map(pair => {
        const pl = playerMap.get(pair.player_id);
        return {
          player_id: pair.player_id,
          first_name: pl?.first_name ?? '?',
          last_name: pl?.last_name ?? '?',
          category: pl?.category ?? '',
          handicap: pl?.handicap ?? 0,
          tee_time: pair.tee_time,
        };
      });

      setPlayers(groupPlayers);
      if (pairings?.[0]?.tee_time) setTeeTime(pairings[0].tee_time.substring(0, 5));
      if (pairings?.[0]?.starting_hole) setStartingHole(pairings[0].starting_hole);

      const { data: existingScores } = await supabase
        .from('hole_scores')
        .select('player_id, hole, strokes, score_status')
        .eq('tournament_id', tournamentId)
        .eq('round', resolvedRoundNumber)
        .in('player_id', playerIds);

      const remote: HoleScores = {};
      for (const pid of playerIds) remote[pid] = {};
      for (const hs of existingScores ?? []) {
        if (!remote[hs.player_id]) remote[hs.player_id] = {};
        remote[hs.player_id][hs.hole] = String(hs.strokes);
      }
      setRemoteScores(remote);

      const draft = loadLocalDraft(draftKey);
      if (draft) {
        const merged: HoleScores = {};
        for (const pid of playerIds) {
          merged[pid] = { ...(remote[pid] ?? {}), ...(draft[pid] ?? {}) };
        }
        setHoleScores(merged);
        setHasDraft(true);
      } else {
        setHoleScores(remote);
        setHasDraft(false);
      }

      // Load existing penalties
      const { data: penaltyRows } = await supabase
        .from('penalties')
        .select('player_id, hole, penalty_strokes, reason')
        .eq('tournament_id', tournamentId)
        .eq('round', resolvedRoundNumber)
        .in('player_id', playerIds);

      const penaltyMap: PenaltyMap = {};
      for (const row of penaltyRows ?? []) {
        if (!penaltyMap[row.player_id]) penaltyMap[row.player_id] = {};
        penaltyMap[row.player_id][row.hole] = {
          penalty_strokes: row.penalty_strokes,
          reason: row.reason,
        };
      }
      setPenalties(penaltyMap);
    } catch {
      toast.error('Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const setStroke = (playerId: string, hole: number, value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    setHoleScores(prev => ({
      ...prev,
      [playerId]: { ...(prev[playerId] ?? {}), [hole]: digits },
    }));
  };

  const openPenaltyModal = (playerId: string, hole: number) => {
    const existing = penalties[playerId]?.[hole];
    setPenaltyStrokes(existing?.penalty_strokes ?? 1);
    setPenaltyReason(existing?.reason ?? '');
    setPenaltyModal({ playerId, hole });
  };

  const savePenalty = async () => {
    if (!penaltyModal) return;
    const { playerId, hole } = penaltyModal;
    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('penalties')
      .upsert({
        tournament_id: tournamentId,
        round: resolvedRoundNumber,
        player_id: playerId,
        hole,
        penalty_strokes: penaltyStrokes,
        reason: penaltyReason.trim(),
        recorded_by: user?.id ?? null,
      }, { onConflict: 'tournament_id,round,player_id,hole' });

    if (error) {
      toast.error('Failed to save penalty');
      return;
    }

    setPenalties(prev => ({
      ...prev,
      [playerId]: {
        ...(prev[playerId] ?? {}),
        [hole]: { penalty_strokes: penaltyStrokes, reason: penaltyReason.trim() },
      },
    }));
    setPenaltyModal(null);
    toast.success('Penalty recorded');
  };

  const removePenalty = async () => {
    if (!penaltyModal) return;
    const { playerId, hole } = penaltyModal;

    await supabase
      .from('penalties')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('round', resolvedRoundNumber)
      .eq('player_id', playerId)
      .eq('hole', hole);

    setPenalties(prev => {
      const updated = { ...prev };
      if (updated[playerId]) {
        const playerPenalties = { ...updated[playerId] };
        delete playerPenalties[hole];
        updated[playerId] = playerPenalties;
      }
      return updated;
    });
    setPenaltyModal(null);
    toast.success('Penalty removed');
  };

  const holeComplete = (hole: number) =>
    players.length > 0 && players.some(pl => {
      const v = holeScores[pl.player_id]?.[hole];
      return v !== undefined && v !== '';
    });

  const totalStrokes = (playerId: string) =>
    Object.values(holeScores[playerId] ?? {}).reduce((sum, v) => sum + (parseInt(v) || 0), 0);

  const holesEntered = (playerId: string) =>
    Object.values(holeScores[playerId] ?? {}).filter(v => v !== '').length;

  const incompleteHoles = () => {
    const holes = assignedHoles.size > 0
      ? [...assignedHoles].sort((a, b) => a - b)
      : Array.from({ length: 18 }, (_, i) => i + 1);
    const missing: number[] = [];
    for (const h of holes) if (!holeComplete(h)) missing.push(h);
    return missing;
  };

  const requiredHoleCount = assignedHoles.size > 0 ? assignedHoles.size : 18;
  const allComplete = players.length > 0 && incompleteHoles().length === 0;

  const detectConflicts = useCallback(() => {
    const found: { playerId: string; hole: number }[] = [];
    for (const player of players) {
      for (let h = 1; h <= 18; h++) {
        const local = holeScores[player.player_id]?.[h];
        const remote = remoteScores[player.player_id]?.[h];
        if (local && remote && local !== remote) found.push({ playerId: player.player_id, hole: h });
      }
    }
    setConflicts(found);
    return found;
  }, [holeScores, remoteScores, players]);

  const handleSave = async (submit = false, silent = false) => {
    if (players.length === 0) return;


    const { data: latestScores } = await supabase
      .from('hole_scores')
      .select('player_id, hole, strokes, score_status')
      .eq('tournament_id', tournamentId)
      .eq('round', resolvedRoundNumber)
      .in('player_id', players.map(pl => pl.player_id));

    const latestRemote: HoleScores = {};
    for (const pl of players) latestRemote[pl.player_id] = {};
    for (const hs of latestScores ?? []) {
      if (!latestRemote[hs.player_id]) latestRemote[hs.player_id] = {};
      latestRemote[hs.player_id][hs.hole] = String(hs.strokes);
      if (hs.score_status === 'verified') {
        if (!silent) toast.error('Scores for this group have been verified and cannot be changed');
        return;
      }
    }
    setRemoteScores(latestRemote);

    const found = detectConflicts();
    if (found.length > 0 && !submit && !silent) {
      toast(`${found.length} score${found.length > 1 ? 's' : ''} differ from previously saved data — your values will be kept`, { icon: '⚠️' });
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const status = submit ? 'submitted' : 'draft';
      const gn = parseInt(groupNumber ?? '0');

      const upserts: object[] = [];
      for (const player of players) {
        const scores = holeScores[player.player_id] ?? {};
        for (const [holeStr, strokeStr] of Object.entries(scores)) {
          const hole = parseInt(holeStr);
          const strokes = parseInt(strokeStr);
          if (!strokeStr || isNaN(strokes) || strokes < 1) continue;
          if (assignedHoles.size > 0 && !assignedHoles.has(hole)) continue;
          upserts.push({
            tournament_id: tournamentId,
            round_id: resolvedRoundId,
            round: resolvedRoundNumber,
            player_id: player.player_id,
            group_number: gn,

            hole,
            strokes,
            submitted_by: user?.id ?? null,
            score_status: status,
            submitted_at: submit ? now : null,
          });
        }
      }

      if (upserts.length === 0) {
        if (!silent) toast.error('No scores entered');
        return;
      }

      if (!isOnline) {
        saveLocalDraft(draftKey, holeScores);
        setHasDraft(true);
        if (!silent) toast('Saved offline — will sync when connection is restored', { icon: '📶' });
        return;
      }

      const { error } = await supabase
        .from('hole_scores')
        .upsert(upserts, { onConflict: 'player_id,tournament_id,round,hole' });

      if (error) throw new Error(error.message);

      clearLocalDraft(draftKey);
      setHasDraft(false);
      setConflicts([]);

      if (submit) {
        toast.success('Scorecard submitted');
        navigate(`${p}/scorer/groups`);
      } else if (!silent) {
        toast.success('Progress saved');
      }
    } catch (err: unknown) {
      saveLocalDraft(draftKey, holeScores);
      setHasDraft(true);
      const msg = err instanceof Error ? err.message
        : (err && typeof err === 'object' && 'message' in err) ? String((err as { message: string }).message)
        : 'Failed to save scores';
      toast.error(msg + ' — saved locally');
    } finally {
      setSaving(false);
      setShowSubmitConfirm(false);
    }
  };

  useEffect(() => {
    if (isOnline && hasDraft && !loading) handleSave(false);
  }, [isOnline]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const missingHoles = incompleteHoles();
  const completedHoles = requiredHoleCount - missingHoles.length;

  return (
    <div className="pb-36">
      {/* Header */}
      <div className="bg-golf-gray px-4 pt-4 pb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors mb-3"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-white">Group {groupNumber}</h1>
            <div className="flex items-center gap-3 mt-1.5">
              {teeTime && (
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-300">{teeTime}</span>
                </div>
              )}
              {startingHole > 1 && (
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-500/30 text-blue-200">H{startingHole}</span>
              )}
              <span className="text-sm text-gray-400">{players.length} players · Round {resolvedRoundNumber || 1}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            {!isOnline && (
              <div className="flex items-center gap-1 bg-red-500/20 text-red-300 rounded-lg px-2 py-1">
                <WifiIcon className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Offline</span>
              </div>
            )}
            {hasDraft && isOnline && (
              <div className="flex items-center gap-1 bg-amber-500/20 text-amber-300 rounded-lg px-2 py-1">
                <CloudArrowUpIcon className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Unsaved</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">{completedHoles}/{requiredHoleCount} holes entered</span>
            <span className="text-xs text-gray-400">{Math.round(completedHoles / requiredHoleCount * 100)}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-golf-orange rounded-full transition-all duration-300"
              style={{ width: `${(completedHoles / requiredHoleCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Status banners */}
      {!isOnline && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
          <WifiIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-700">
            No internet — scores saved locally and will sync when you reconnect.
          </p>
        </div>
      )}
      {conflicts.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            {conflicts.length} score{conflicts.length > 1 ? 's differ' : ' differs'} from a previously saved version. Your current entries will be kept.
          </p>
        </div>
      )}

      {/* Hole selector */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const holes = assignedHoles.size > 0
                ? [...assignedHoles].sort((a, b) => a - b)
                : Array.from({ length: 18 }, (_, i) => i + 1);
              const idx = holes.indexOf(activeHole);
              if (idx > 0) setActiveHole(holes[idx - 1]);
            }}
            disabled={(() => {
              const holes = assignedHoles.size > 0
                ? [...assignedHoles].sort((a, b) => a - b)
                : Array.from({ length: 18 }, (_, i) => i + 1);
              return holes.indexOf(activeHole) === 0;
            })()}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-30 flex-shrink-0"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
          </button>

          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5 min-w-max">
              {(assignedHoles.size > 0
                ? [...assignedHoles].sort((a, b) => a - b)
                : Array.from({ length: 18 }, (_, i) => i + 1)
              ).map(h => (
                <button
                  key={h}
                  onClick={() => setActiveHole(h)}
                  className={`w-9 h-9 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                    activeHole === h
                      ? 'bg-golf-orange text-white shadow-sm scale-105'
                      : holeComplete(h)
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-golf-orange/15 text-golf-orange'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => {
              const holes = assignedHoles.size > 0
                ? [...assignedHoles].sort((a, b) => a - b)
                : Array.from({ length: 18 }, (_, i) => i + 1);
              const idx = holes.indexOf(activeHole);
              if (idx < holes.length - 1) setActiveHole(holes[idx + 1]);
            }}
            disabled={(() => {
              const holes = assignedHoles.size > 0
                ? [...assignedHoles].sort((a, b) => a - b)
                : Array.from({ length: 18 }, (_, i) => i + 1);
              return holes.indexOf(activeHole) === holes.length - 1;
            })()}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-30 flex-shrink-0"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-3">
        {/* Missing holes warning */}
        {missingHoles.length > 0 && missingHoles.length < 18 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Missing scores: holes {missingHoles.slice(0, 6).join(', ')}{missingHoles.length > 6 ? ` +${missingHoles.length - 6} more` : ''}
            </p>
          </div>
        )}

        {/* Unassigned hole warning */}
        {assignedHoles.size > 0 && !assignedHoles.has(activeHole) && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500">
              Hole {activeHole} is not assigned to you — scores are read-only.
            </p>
          </div>
        )}

        {/* Current hole score entry */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-golf-gray">Hole {activeHole}</p>
              {assignedHoles.size > 0 && assignedHoles.has(activeHole) && (
                <span className="text-xs bg-golf-orange/10 text-golf-orange font-medium px-1.5 py-0.5 rounded">Your hole</span>
              )}
            </div>
            {holeComplete(activeHole) && (
              <div className="flex items-center gap-1">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-emerald-600 font-medium">Complete</span>
              </div>
            )}
          </div>

          <div className="divide-y divide-gray-50">
            {players.map((player, idx) => {
              const val = holeScores[player.player_id]?.[activeHole] ?? '';
              const refKey = `${player.player_id}-${activeHole}`;
              const hasConflict = conflicts.some(c => c.playerId === player.player_id && c.hole === activeHole);
              const canEdit = assignedHoles.size === 0 || assignedHoles.has(activeHole);
              return (
                <div key={player.player_id} className={`flex items-center gap-3 px-4 py-3.5 ${hasConflict ? 'bg-amber-50' : ''}`}>
                  <div className="w-9 h-9 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-golf-orange">
                      {player.first_name[0]}{player.last_name[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray truncate">
                      {player.first_name} {player.last_name}
                    </p>
                    <p className="text-xs text-gray-400">{player.category} · HC {player.handicap}</p>
                  </div>
                  <input
                    ref={el => { inputRefs.current[refKey] = el; }}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    value={val}
                    readOnly={!canEdit}
                    onChange={e => canEdit && setStroke(player.player_id, activeHole, e.target.value)}
                    onFocus={e => canEdit && e.target.select()}
                    onKeyDown={e => {
                      if (!canEdit) return;
                      if (e.key === 'Enter') {
                        if (idx < players.length - 1) {
                          inputRefs.current[`${players[idx + 1].player_id}-${activeHole}`]?.focus();
                        } else {
                          const holes = assignedHoles.size > 0
                            ? [...assignedHoles].sort((a, b) => a - b)
                            : Array.from({ length: 18 }, (_, i) => i + 1);
                          const hIdx = holes.indexOf(activeHole);
                          if (hIdx < holes.length - 1) setActiveHole(holes[hIdx + 1]);
                        }
                      }
                    }}
                    placeholder="—"
                    className={`w-14 h-12 text-center border rounded-xl text-base font-bold text-golf-gray focus:outline-none bg-gray-50 transition-colors ${
                      !canEdit
                        ? 'opacity-40 cursor-not-allowed border-gray-100'
                        : hasConflict
                        ? 'border-amber-400 focus:border-amber-500'
                        : 'border-gray-200 focus:border-golf-orange focus:bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => canEdit && openPenaltyModal(player.player_id, activeHole)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                      penalties[player.player_id]?.[activeHole]
                        ? 'bg-red-50 text-red-500'
                        : canEdit
                        ? 'bg-gray-50 text-gray-300 active:bg-gray-100'
                        : 'opacity-40 cursor-not-allowed bg-gray-50 text-gray-200'
                    }`}
                    title="Penalty"
                  >
                    <FlagIcon className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {(() => {
            const holes = assignedHoles.size > 0
              ? [...assignedHoles].sort((a, b) => a - b)
              : Array.from({ length: 18 }, (_, i) => i + 1);
            const idx = holes.indexOf(activeHole);
            const nextHole = idx < holes.length - 1 ? holes[idx + 1] : null;
            if (!nextHole) return null;
            return (
              <div className="px-4 pb-3">
                <button
                  onClick={() => setActiveHole(nextHole)}
                  disabled={!holeComplete(activeHole)}
                  className="w-full py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold disabled:opacity-40 active:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                >
                  Next: Hole {nextHole}
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            );
          })()}
        </div>

        {/* Running totals with net score */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scorecard</p>
            <p className="text-xs text-gray-400">Gross · {scoringMethod === 'stableford_net' ? 'Pts' : 'Net'}</p>
          </div>
          <div className="divide-y divide-gray-50">
            {players.map(player => {
              const gross = totalStrokes(player.player_id);
              const entered = holesEntered(player.player_id);
              const net = calcNetScore(gross, entered, player.handicap, scoringMethod);
              return (
                <div key={player.player_id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                  <p className="text-sm text-golf-gray flex-1 truncate">{player.first_name} {player.last_name}</p>
                  <div className="text-right flex-shrink-0 flex items-center gap-3">
                    <div>
                      <p className="text-sm font-bold text-golf-gray">{gross || '—'}</p>
                      <p className="text-xs text-gray-400">{entered}/18</p>
                    </div>
                    <div className="w-px h-8 bg-gray-100" />
                    <div>
                      <p className={`text-sm font-bold ${netScoreColor(net, scoringMethod)}`}>{netScoreLabel(net, scoringMethod)}</p>
                      <p className="text-xs text-gray-400">{scoringMethod === 'stableford_net' ? 'Pts' : 'Net'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3">
        <button
          onClick={() => setShowSubmitConfirm(true)}
          disabled={saving}
          className={`w-full py-3 rounded-xl text-sm font-bold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
            allComplete ? 'bg-emerald-500 active:bg-emerald-600' : 'bg-golf-orange active:bg-golf-orange/90'
          }`}
        >
          <CheckCircleIcon className="w-4 h-4" />
          {allComplete ? 'Submit Scorecard' : `${completedHoles}/${requiredHoleCount} Holes`}
        </button>
      </div>

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl p-6">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-base font-bold text-golf-gray text-center mb-2">Submit Scorecard?</h3>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-2">
              {players.map(player => {
                const gross = totalStrokes(player.player_id);
                const net = calcNetScore(gross, holesEntered(player.player_id), player.handicap, scoringMethod);
                return (
                  <div key={player.player_id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{player.first_name} {player.last_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{gross} gross</span>
                      <span className={`text-sm font-bold ${netScoreColor(net, scoringMethod)}`}>{netScoreLabel(net, scoringMethod)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 text-center mb-5">
              Scores will be marked submitted. An official will verify them. You can still edit until verified.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Penalty modal */}
      {penaltyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setPenaltyModal(null)}>
          <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FlagIcon className="w-5 h-5 text-red-500" />
              <h3 className="text-base font-bold text-golf-gray">
                Penalty — Hole {penaltyModal.hole}
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {players.find(p => p.player_id === penaltyModal.playerId)?.first_name}{' '}
              {players.find(p => p.player_id === penaltyModal.playerId)?.last_name}
            </p>

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Penalty Strokes
            </label>
            <div className="flex items-center gap-3 mb-4">
              {[1, 2, 3, 4].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPenaltyStrokes(n)}
                  className={`w-11 h-11 rounded-xl text-sm font-bold transition-colors ${
                    penaltyStrokes === n
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  }`}
                >
                  +{n}
                </button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Reason
            </label>
            <textarea
              value={penaltyReason}
              onChange={e => setPenaltyReason(e.target.value)}
              placeholder="e.g. Out of bounds, water hazard, lost ball..."
              rows={2}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-golf-gray placeholder:text-gray-300 focus:outline-none focus:border-golf-orange mb-5 resize-none"
            />

            <div className="flex gap-3">
              {penalties[penaltyModal.playerId]?.[penaltyModal.hole] && (
                <button
                  type="button"
                  onClick={removePenalty}
                  className="flex-1 py-3 rounded-xl border border-red-200 text-sm font-semibold text-red-500 active:bg-red-50"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={savePenalty}
                disabled={!penaltyReason.trim()}
                className="flex-[2] py-3 rounded-xl bg-red-500 text-white text-sm font-bold disabled:opacity-40 active:bg-red-600"
              >
                {penalties[penaltyModal.playerId]?.[penaltyModal.hole] ? 'Update Penalty' : 'Add Penalty'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
